import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box } from 'lucide-react';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton } from '@/shared/ui';

import type { RootElement } from '../model/types';
import { useRootElements } from '../queries/useElement';
import styles from './ConstellationEntry.module.css';

/**
 * Punkt wejścia do konstelacji — trasa `/constellation` **bez identyfikatora**.
 *
 * ⚠ Powstało z usterki zgłoszonej z ekranu: pozycja „Constellation" w menu prowadzi
 * właśnie tutaj, a takiej trasy nie było — żądanie wpadało w `path: '*'` i cicho
 * lądowało na pulpicie. Ciche przekierowanie jest gorsze od pustego ekranu, bo
 * wygląda jak awaria, a nie jak brak wyboru.
 *
 * Pokazujemy **korzenie grafu** (elementy bez rodzica po `has`) w DWÓCH listach
 * (2026-08-23, słowo usera): **punkty wejścia** — korzenie dodane przez innych (import,
 * administrator, zespół), czyli spis treści wspólnej mapy — oraz **moje elementy bez
 * rodzica** — to, co sam dodałem i co jeszcze w niczym nie siedzi (szkice z omniboksa,
 * huby, które czekają na miejsce). Podział robi ekran po fladze `createdByMe` z jednej
 * listy; serwer stawia moje korzenie na początku, żeby limit nie obciął ich za cudzymi.
 */
export function ConstellationEntryScreen() {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const roots = useRootElements();

  if (roots.isPending) {
    return (
      <div className={styles['screen']} aria-busy="true">
        <Skeleton width="18rem" height="1.5rem" announce />
        <Skeleton height="2.5rem" />
        <Skeleton height="2.5rem" />
        <Skeleton height="2.5rem" />
      </div>
    );
  }

  if (roots.isError) {
    return (
      <div className={styles['failure']} role="alert">
        <p>{describeError(roots.error)}</p>
        <Button onClick={() => void roots.refetch()}>{t('common.action.retry')}</Button>
      </div>
    );
  }

  const mine = roots.data.filter((element) => element.createdByMe);
  const others = roots.data.filter((element) => !element.createdByMe);

  return (
    <div className={styles['screen']}>
      <h1 className={styles['heading']} id="constellation-entry-heading">
        {t('graph.entry.heading')}
      </h1>
      <p className={styles['lead']}>{t('graph.entry.lead')}</p>

      {roots.data.length === 0 ? (
        <p className={styles['empty']}>{t('graph.entry.empty')}</p>
      ) : (
        <>
          {/* ⭐ Moje NAJPIERW, gdy są: to, co sam dodałem, jest bliżej ręki niż spis treści całości —
              a spis treści bywa długi. Gdy nie mam nic własnego, sekcja nie rysuje pustej ramki. */}
          {mine.length > 0 ? (
            <RootsSection
              id="constellation-entry-mine"
              heading={t('graph.entry.mine.heading')}
              hint={t('graph.entry.mine.hint')}
              elements={mine}
            />
          ) : null}

          <RootsSection
            id="constellation-entry-others"
            heading={t('graph.entry.others.heading')}
            hint={t('graph.entry.others.hint')}
            elements={others}
            emptyText={t('graph.entry.others.empty')}
          />
        </>
      )}
    </div>
  );
}

interface RootsSectionProps {
  id: string;
  heading: string;
  hint: string;
  elements: RootElement[];
  emptyText?: string;
}

function RootsSection({ id, heading, hint, elements, emptyText }: RootsSectionProps) {
  return (
    <section className={styles['section']} aria-labelledby={id}>
      <div className={styles['sectionHead']}>
        <h2 id={id} className={styles['sectionHeading']}>
          {heading}
          <span className={styles['count']}>{elements.length}</span>
        </h2>
        <p className={styles['hint']}>{hint}</p>
      </div>

      {elements.length === 0 ? (
        <p className={styles['empty']}>{emptyText}</p>
      ) : (
        // Lista nazwana nagłówkiem: na stronie są DWIE listy (i bywają kolejne — choćby
        // `<ol>` powiadomień Radiksa), a czytnik ekranu rozróżnia je wyłącznie nazwą.
        <ul className={styles['list']} aria-labelledby={id}>
          {elements.map((element) => (
            <li key={element.id}>
              <Link className={styles['item']} to={`/constellation/${element.id}`}>
                {/* Znak mówi „to jest element" — ten sam co przy trafieniach omniboksa.
                    ⛔ Nie znak czcionki: `■` rysowała czcionka systemowa (spec 12B.7). */}
                <span className={styles['mark']} aria-hidden="true">
                  <Box size={14} />
                </span>
                <span className={styles['title']}>{element.title}</span>
                {element.type ? <span className={styles['type']}>{element.type}</span> : null}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
